import React, { useState } from 'react';
import classnames from 'classnames';

import { HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD } from 'common/householdConstants';

import {
  SR_API_ACTION_HOUSEHOLD_REMOVE_USER,
  SR_API_ACTION_HOUSEHOLD_REMOVE_GUARANTOR,
} from 'api/actionTypes';

import { formatPrice } from 'common/formatters';

import {
  isApplicationPastStatus,
  findHouseholdApplicationSubmission,
} from 'common/householdApplication/applicationHelpers';

import postWithState from 'client/actions/postWithState';
import HouseholdApplicationActions from 'client/actions/HouseholdApplication';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import {
  BUTTON_THEME_RED_ADOBE,
  BUTTON_THEME_GREY_ADOBE,
} from 'client/components/ButtonDeprecated';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrButtonDeprecated from 'client/components/SrButtonDeprecated';
import SrRenterApplicationStatusRoommatesListRow from 'client/components/SrRenterApplicationStatusRoommatesListRow';
import SrRenterApplicationInviteRoommateListRowSunroom from 'client/components/SrRenterApplicationInviteRoommateListRowSunroom';

const VIEW_MODE_EDIT_APP = 'editApp';
const VIEW_MODE_REMOVE_APPS = 'removeApps';

const SrRenterApplicationStatusStepApplicants = ({
  listing,
  householdListing,
  roommateApplications,
  guarantorApplications,
  householdGuarantors,
  applicationSubmissions,
  dispatch,
  history,
  currentUser,
  onInviteRoommateClicked,
  applicationRejected,
  viewingUserId,
  earliestApplication,
  InviteGuarantorLabel,
}) => {
  const [viewMode, setViewMode] = useState(VIEW_MODE_EDIT_APP);
  const [removeApp, setRemoveApp] = useState(null);

  const onRemoveApp = () => {
    dispatch(postWithState, {
      action: removeApp.guarantorUserId
        ? SR_API_ACTION_HOUSEHOLD_REMOVE_GUARANTOR
        : SR_API_ACTION_HOUSEHOLD_REMOVE_USER,
      payload: removeApp,
      onSuccess: () => {
        setRemoveApp(null);

        dispatch(HouseholdApplicationActions.getHouseholdApplicationsByUserId, {
          userId: currentUser.id,
        }).then((householdApplications) => {
          const { roommateApplications, guarantorApplications } =
            householdApplications.find(
              (householdApplication) => householdApplication.listing.id === listing.id,
            ) || {};
          const appsCounts = roommateApplications?.length + guarantorApplications?.length;
          if (appsCounts === 1) {
            setViewMode(VIEW_MODE_EDIT_APP);
          }
        });
      },
    });
  };

  return (
    <>
      <div className={classnames('SrRenterApplicationStatusDescription', 'descriptionText')}>
        All adults aged 18 or older must complete their application forms and pay a{' '}
        {formatPrice(listing.applicationFee)} application fee. See the progress of everyone in your
        household below.
      </div>
      {InviteGuarantorLabel && <InviteGuarantorLabel marginBottom="-7px" />}
      <div className="SrRenterApplicationStatusSubheader">your household</div>
      <div className="SrRenterApplicationStatusRoommatesList">
        {roommateApplications.map((app) => (
          <SrRenterApplicationStatusRoommatesListRow
            key={`srasrlr-${app.id}`}
            application={app}
            dispatch={dispatch}
            history={history}
            currentUser={currentUser}
            listing={listing}
            disabled={applicationRejected || viewingUserId}
            showRemoveButton={viewMode === VIEW_MODE_REMOVE_APPS}
            onRemove={() =>
              setRemoveApp({
                userId: app.userId,
                householdId: app.householdId,
              })
            }
          />
        ))}
        {guarantorApplications.map((app) => (
          <SrRenterApplicationStatusRoommatesListRow
            key={`srasrlr-grtor-${app.id}`}
            application={app}
            applicationSubmission={findHouseholdApplicationSubmission({
              applicationSubmissions,
              applicationId: app.id,
              listingId: listing.id,
            })}
            dispatch={dispatch}
            history={history}
            currentUser={currentUser}
            listing={listing}
            disabled={applicationRejected || viewingUserId}
            isGuarantor
            showRemoveButton={viewMode === VIEW_MODE_REMOVE_APPS}
            onRemove={() => {
              const guarantor = householdGuarantors.find(
                ({ guarantorUserId }) => guarantorUserId === app.userId,
              );
              setRemoveApp(guarantor);
            }}
          />
        ))}
      </div>
      {!isApplicationPastStatus(
        earliestApplication,
        HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
      ) &&
        !viewingUserId &&
        !applicationRejected &&
        onInviteRoommateClicked && (
          <>
            {viewMode === VIEW_MODE_EDIT_APP && (
              <>
                <SrRenterApplicationInviteRoommateListRowSunroom
                  listing={listing}
                  householdId={householdListing.householdId}
                  onInviteRoommateClicked={onInviteRoommateClicked}
                />
                {(roommateApplications.length >= 2 || guarantorApplications.length >= 1) && (
                  <SrBox
                    textAlign="center"
                    margin="18px 0"
                    cursor="pointer"
                    onClick={() => setViewMode(VIEW_MODE_REMOVE_APPS)}
                  >
                    <SrText font="whitneySemibold" color="red" weight="500" lineHeight="28px">
                      Remove Applicant/Guarantor
                    </SrText>
                  </SrBox>
                )}
              </>
            )}
            {viewMode === VIEW_MODE_REMOVE_APPS && (
              <SrBox>
                <SrButtonDeprecated
                  className="blue large"
                  text="close"
                  onClick={() => setViewMode(VIEW_MODE_EDIT_APP)}
                />
              </SrBox>
            )}
          </>
        )}
      {removeApp && (
        <SrSimpleMessageModal
          headline="Remove from Household?"
          underline
          message="Are you sure you want to remove this applicant from your household?"
          multiButtons={[
            {
              buttonText: 'Remove',
              buttonTheme: BUTTON_THEME_RED_ADOBE,
              onButtonClick: onRemoveApp,
            },
            {
              buttonText: 'Cancel',
              buttonTheme: BUTTON_THEME_GREY_ADOBE,
              onButtonClick: () => setRemoveApp(null),
            },
          ]}
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          maxWidth={380}
        />
      )}
    </>
  );
};

export default SrRenterApplicationStatusStepApplicants;
